import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View, TouchableOpacity, Modal, TextInput, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import { faCheck, faChevronDown, faChevronUp, faLocationDot, faPencil, faPeopleGroup, faSearchPlus, faTimes, faXmark, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import { faCameraCctv } from '@fortawesome/pro-solid-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import OccurrenceVideoModal from './OccurrenceVideoModal';
import { dispatchService } from '../../../services/central-api/dispatch';
import DragAndZoom from '../../DragAndZoom';
import { InfoInRow } from '../../InfoInRow';
import { TriggerType } from '../../../schemas/dispatch';
import { faMobile } from '@fortawesome/pro-regular-svg-icons';

const regPlate = new RegExp(/^[A-Z]{3}-?(?:[0-9][0-9A-Z][0-9]{2}|49CC)$/);


function isCitizenLprDetection(event: LprDetection | CitizenLprDetection): event is CitizenLprDetection {
    return (event as CitizenLprDetection).citizenId !== undefined;
}

export default function OccurrenceFormLprDetected({
    event, navigation, setEvent, authenticatedUser, triggerType, occurrenceId, occurrenceImage, address
}: {
    event: LprDetection | CitizenLprDetection;
    setEvent: (LprDetection: LprDetection | CitizenLprDetection) => void;
    navigation: Navigation;
    authenticatedUser?: AuthenticatedUser;
    triggerType?: TriggerType;
    occurrenceId: number;
    occurrenceImage: string;
    address?: string;
}) {
    const { styles, theme } = useStyles(stylesheet);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const zoomCanvasRef = useRef<HTMLCanvasElement>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const windowInfo = WindowInformation();
    const [editingPlate, setEditingPlate] = useState(false);
    const [newPlate, setNewPlate] = useState(event.plate);
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [isVideoModalVisible, setVideoModalVisible] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>();
    const [isImageLoading, setIsImageLoading] = useState<boolean>(false);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas || !occurrenceImage) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        if (!event) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;

            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            if (event.platePosition) {
                for (const position of event.platePosition) {
                    if (position.x < rect.minX) {
                        rect.minX = position.x;
                    }

                    if (position.x > rect.maxX) {
                        rect.maxX = position.x;
                    }

                    if (position.y < rect.minY) {
                        rect.minY = position.y;
                    }

                    if (position.y > rect.maxY) {
                        rect.maxY = position.y;
                    }
                }

                ctx.beginPath();
                ctx.moveTo(rect.minX, rect.minY);
                ctx.lineTo(rect.minX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.minY);
                ctx.closePath();
                ctx.stroke();
            }

        };

        fetchImageAsBlob(occurrenceImage).then(imageUrl => {
            setImageUrl(imageUrl);
            image.src = imageUrl;
        });

    }, [canvasRef, occurrenceImage]);

    async function fetchImageAsBlob(url: string) {
        const res = await fetch(url);
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    }

    useEffect(() => {
        const canvas = zoomCanvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx || !imageUrl) {
            return;
        }

        if (!event) {
            return;
        }

        setIsImageLoading(true);
        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;

            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            if (event.platePosition) {
                for (const position of event.platePosition) {
                    if (position.x < rect.minX) {
                        rect.minX = position.x;
                    }

                    if (position.x > rect.maxX) {
                        rect.maxX = position.x;
                    }

                    if (position.y < rect.minY) {
                        rect.minY = position.y;
                    }

                    if (position.y > rect.maxY) {
                        rect.maxY = position.y;
                    }
                }

                ctx.beginPath();
                ctx.moveTo(rect.minX, rect.minY);
                ctx.lineTo(rect.minX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.minY);
                ctx.closePath();
                ctx.stroke();
            }

            setIsImageLoading(false);
        };

        image.onerror = () => {
            setIsImageLoading(false);
        };

        image.src = imageUrl;

    }, [zoomCanvasRef, isModalVisible, imageUrl]);

    function isPlateInvalid(value: string) {
        return value == '' || value.length > 7 || !regPlate.test(value);
    }

    async function saveNewPlate() {

        if (isPlateInvalid(newPlate)) {
            return Toast.show({
                type: 'sentinelxError',
                text1: translate('invalidPlate'),
            });
        }

        try {
            const detection = await licensePlateRecognitionService.editLprDetection(event.id, newPlate);
            setEvent(detection);
            setEditingPlate(false);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <ScrollView contentContainerStyle={{ rowGap: 10 }}>
            <View>
                <MyAppText style={styles.plateText}>
                    {translate('plate')}
                </MyAppText>
                <View style={{ flexDirection: 'row', gap: 5 }}>
                    {editingPlate ?
                        <>
                            <TextInput
                                placeholder=''
                                style={{
                                    fontSize: 24,
                                    fontWeight: 'bold',
                                    width: 120
                                }}
                                value={newPlate}
                                onChangeText={(value) => {
                                    if (value.length > 7) {
                                        return;
                                    }
                                    setNewPlate(value.toUpperCase());
                                }}
                            />
                            <TouchableOpacity
                                onPress={() => {
                                    setNewPlate(event.plate);
                                    setEditingPlate(false);
                                }}
                            >
                                <FontAwesomeIcon color={theme.colors.iconColor} icon={faXmark} fontSize={20} />
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={saveNewPlate}
                            >
                                <FontAwesomeIcon color={theme.colors.iconColor} icon={faCheck} fontSize={20} />
                            </TouchableOpacity>
                        </>
                        :
                        <>
                            <MyAppText style={styles.plateInfoText}>
                                {event.plate}
                            </MyAppText>
                            {authenticatedUser?.isAdmin || authenticatedUser?.permissions.lpr_edit_detection ?
                                <TouchableOpacity
                                    onPress={() => setEditingPlate(true)}
                                >
                                    <FontAwesomeIcon color={theme.colors.iconColor} icon={faPencil} fontSize={16} />
                                </TouchableOpacity>
                                : null
                            }
                        </>
                    }
                </View>
                <TouchableOpacity
                    style={{ flex: 1 }}
                    onPress={async () => {
                        setIsModalVisible(true);
                    }}
                >
                    <Hoverable>
                        {({ hovered }) => (<>
                            <canvas style={{ width: '100%' }} ref={canvasRef} />

                            {hovered &&
                                <View style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#00000080',
                                    justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon={faSearchPlus} fontSize={28} color={'#FFFFFF'} />
                                </View>
                            }
                        </>)}
                    </Hoverable>
                </TouchableOpacity>

            </View>

            <View style={styles.generalInformation}>
                <TouchableOpacity
                    style={styles.generalInformationDropdown}
                    onPress={() => setShowDetails(!showDetails)}>
                    <MyAppText>{translate('general_info')}</MyAppText>
                    <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} color={theme.colors.iconColor} />
                </TouchableOpacity>
                <View style={styles.tableLine}></View>
            </View>
            {showDetails ? <>
                {triggerType != 'plate_detected_by_mobile_agent' && triggerType != 'plate_detected_by_citizen_app' && <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }} >
                    <FontAwesomeIcon icon={faPlay as IconDefinition} style={{ width: 22 }} color={theme.colors.iconColor} />
                    <TouchableOpacity onPress={() => setVideoModalVisible(true)}>
                        <MyAppText style={styles.watchVideoText}>
                            {translate('watchVideo')}
                        </MyAppText>
                    </TouchableOpacity>
                </View>}
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('vehicleData')}
                    </MyAppText>
                    <View style={{ flexDirection: 'column', gap: 5 }} >
                        <InfoInRow value={event['vehicleDescription.brand']} title='brand' />
                        <InfoInRow value={event['vehicleDescription.model']} title='model' />
                        <InfoInRow value={event['vehicleDescription.color']} title='color' />
                        <InfoInRow value={event['vehicleDescription.type']} title='vehicleType' />
                        <InfoInRow value={event['vehicleDescription.city']} title='origin' />
                        <InfoInRow value={event['vehicleDescription.modelYear']} title='year' />
                    </View>
                </View>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {triggerType == 'plate_detected_by_mobile_agent' || triggerType == 'plate_detected_by_citizen_app' ? translate('capturedIn') : translate('visualizedOn')}
                    </MyAppText>
                    {triggerType === 'plate_detected_by_citizen_app' && isCitizenLprDetection(event) && <>
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faMobile as IconDefinition} style={{ width: 15 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => {
                                window.open(`/citizen/citizen-lpr-events?citizenIds[]=${event.citizen.id}`);
                            }}>
                                <MyAppText style={[styles.detectionInfoText, { textDecorationLine: 'underline' }]}>
                                    {`${translate('citizen')}: ${event.citizen.name}`}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                        {address !== '' && (
                            <View style={styles.visualizedRow}>
                                <FontAwesomeIcon icon={faLocationDot} style={{ width: 14 }} color={theme.colors.iconColor} />
                                <MyAppText style={styles.detectionInfoText}>
                                    {address}
                                </MyAppText>
                            </View>
                        )}
                    </>
                    }
                    {triggerType === 'plate_detected_by_mobile_agent' && !isCitizenLprDetection(event) && <>
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faPeopleGroup} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {`${translate('team')}: ${event.unitName?.name}`}
                            </MyAppText>
                        </View>
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faCameraCctv as IconDefinition} style={{ width: 22 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => {
                                window.open(`/dispatch/team-names/form/${event.unitName?.id}?selectedTab=live`);
                            }}>
                                <MyAppText style={[styles.detectionInfoText, { textDecorationLine: 'underline' }]}>
                                    {translate('watchLive')}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                    </>
                    }
                    {triggerType != 'plate_detected_by_mobile_agent' && triggerType != 'plate_detected_by_citizen_app' && !isCitizenLprDetection(event) &&
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faCameraCctv as IconDefinition} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => {
                                navigation.navigate('CameraPlayer', { id: event.camera?.id });
                            }}>
                                <MyAppText style={[styles.detectionInfoText, { textDecorationLine: 'underline' }]}>
                                    {event.camera?.title}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                    }
                    <View style={styles.visualizedRow} >
                        <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 14 }} color={theme.colors.iconColor} />
                        <MyAppText style={styles.detectionInfoText}>
                            {moment(new Date(event.time)).format('DD/MM/YYYY HH:mm:ss')}
                        </MyAppText>
                    </View>
                    {triggerType != 'plate_detected_by_mobile_agent' && triggerType != 'plate_detected_by_citizen_app' && !isCitizenLprDetection(event) &&
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faLocationDot} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {event.camera?.address}
                            </MyAppText>
                        </View>
                    }
                </View>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('description')}
                    </MyAppText>
                    <MyAppText style={styles.detectionInfoText}>
                        {event.description}
                    </MyAppText>
                </View></> : <></>}

            <OccurrenceVideoModal
                occurrenceId={occurrenceId || 0}
                occurrenceTime={new Date(event.time).getTime()}
                isModalVisible={isVideoModalVisible}
                setModalVisible={setVideoModalVisible}
                apiService={dispatchService}
            />
            {/* Zoom image modal */}
            <Modal
                animationType='fade'
                transparent={true}
                visible={isModalVisible}
                onRequestClose={() => setIsModalVisible(false)}>
                <View style={styles.modalContainer}>
                    <View style={styles.centeredView}>
                        <View style={[styles.modalBody, windowInfo.isMobile ? { width: '95%' } : { width: '60%' }]}>
                            <View style={{ alignItems: 'flex-end' }}>
                                <TouchableOpacity
                                    style={{ width: 20, height: 20 }}
                                    onPress={() => setIsModalVisible(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} style={{ width: 20 }} color={theme.colors.iconColor} />
                                </TouchableOpacity>
                            </View>
                            {isImageLoading && <ActivityIndicator style={[{ width: '100%', height: '100%' }]} size='large' color={theme.colors.loadingColor} />}
                            <DragAndZoom>
                                <canvas style={isImageLoading ? { width: '100%', opacity: 0 } : { width: '100%', opacity: 1 }} ref={zoomCanvasRef} />
                            </DragAndZoom>
                        </View>
                    </View>
                </View>
            </Modal>
        </ScrollView >
    );
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
    },
    imageText: {
        color: theme.colors.card.detailText,
        paddingBottom: 5,
        fontSize: 11,
    },
    detectionInfoText: {
        color: theme.colors.labelColor,
        fontSize: 12
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalBody: {
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 8,
        rowGap: 20,
        padding: 30,
    },
    plateText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    plateInfoText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 24
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
        flex: 1
    },
    generalInformation: {
        flexDirection: 'column',
        paddingHorizontal: 5,
        flex: 1,
        gap: 10
    },
    generalInformationDropdown: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1
    },
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
    watchVideoText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        textDecorationLine: 'underline'
    },
}));
